import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, Pagination, type Column } from "@atlas/ui/web";
import type { LinhaUsoApi } from "@atlas/sdk";
import { atlas } from "../../../lib/sdk";
import { downloadCsv } from "../../../lib/csv";
import { motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../../lib/recorte";
import { useFiltros } from "../../../lib/use-filtros";

const ESTILO_CAMPO: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border-strong)",
  background: "var(--surface)",
  color: "var(--text)",
  fontSize: 14,
};

/** Últimos 30 dias civis em America/Sao_Paulo — o mesmo fuso do agregado. */
function intervaloPadrao(): { de: string; ate: string } {
  const ate = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [y, m, d] = ate.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() - 29);
  return { de: dt.toISOString().slice(0, 10), ate };
}

function rotuloAmbiente(ambiente: string): string {
  return ambiente === "production" ? "Produção" : "Homologação";
}

export function AverbadoraApiUso() {
  const padrao = useMemo(() => intervaloPadrao(), []);
  const [de, setDe] = useState(padrao.de);
  const [ate, setAte] = useState(padrao.ate);
  const [bancoId, setBancoId] = useState("");
  const [ambiente, setAmbiente] = useState<"" | "production" | "sandbox">("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  const bancoNumerico = /^\d+$/.test(bancoId) ? Number(bancoId) : undefined;

  const data = useQuery({
    queryKey: ["admin", "api-uso", de, ate, bancoNumerico ?? null, ambiente],
    queryFn: () => atlas.admin.usoApi({
      de,
      ate,
      ...(bancoNumerico ? { bancoId: bancoNumerico } : {}),
      ...(ambiente ? { ambiente } : {}),
    }),
  });

  const filtros = useFiltros(
    [
      {
        nome: "periodo",
        ligado: de !== padrao.de || ate !== padrao.ate,
        limpar: () => { setDe(padrao.de); setAte(padrao.ate); },
      },
      { nome: "banco", ligado: bancoId !== "", limpar: () => setBancoId("") },
      { nome: "ambiente", ligado: ambiente !== "", limpar: () => setAmbiente("") },
    ],
    setPagina,
  );

  const linhas = data.data?.linhas ?? [];
  const daPagina = recortar(linhas, pagina, porPagina);
  const totais = data.data?.totais;

  const columns: Column<LinhaUsoApi>[] = [
    { key: "dia", header: "Dia" },
    {
      key: "banco",
      header: "Consignatária",
      render: (r) => r.bancoNome ? `${r.bancoNome} (#${r.bancoId})` : `#${r.bancoId}`,
    },
    {
      key: "token",
      header: "Token",
      render: (r) => (
        <span title={r.tokenId}>{r.tokenNome ?? r.tokenId}</span>
      ),
    },
    { key: "metodo", header: "Método" },
    {
      key: "rota",
      header: "Rota",
      render: (r) => <code style={{ fontSize: 12 }}>{r.rota}</code>,
    },
    { key: "ambiente", header: "Ambiente", render: (r) => rotuloAmbiente(r.ambiente) },
    { key: "total", header: "Chamadas", align: "right" },
    { key: "http2xx", header: "2xx", align: "right" },
    { key: "http4xx", header: "4xx", align: "right" },
    { key: "http5xx", header: "5xx", align: "right" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            API
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Uso de API</h1>
          <p style={{ color: "var(--text-muted)", marginTop: 4, maxWidth: 720 }}>
            Chamadas autenticadas de <code>/v1/external/banco/*</code>, por consignatária, token, rota, método, ambiente e dia.
            O dia é o calendário de Brasília. Token ausente ou inválido não entra.
            Isto não é o <b>Faturamento (comissão)</b> do portal da consignatária — aquele apura comissão sobre contratos e não muda com estas chamadas.
          </p>
          <p style={{ color: "var(--text-muted)", marginTop: 4, fontSize: 13 }}>
            O “Último uso” do token atualiza no máximo a cada 5 minutos.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => downloadCsv("uso-api.csv", linhas.map((l) => ({
            dia: l.dia,
            banco_id: l.bancoId,
            banco: l.bancoNome ?? "",
            token_id: l.tokenId,
            token: l.tokenNome ?? "",
            rota: l.rota,
            metodo: l.metodo,
            ambiente: l.ambiente,
            total: l.total,
            http_2xx: l.http2xx,
            http_3xx: l.http3xx,
            http_4xx: l.http4xx,
            http_5xx: l.http5xx,
          })))}
        >
          Exportar CSV
        </Button>
      </header>

      <FilterBar layout="linha" {...filtros.propsFilterBar}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
          De
          <input type="date" value={de} onChange={(e) => { if (e.target.value) filtros.filtrar(setDe)(e.target.value); }} style={ESTILO_CAMPO} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
          até
          <input type="date" value={ate} onChange={(e) => { if (e.target.value) filtros.filtrar(setAte)(e.target.value); }} style={ESTILO_CAMPO} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
          Consignatária
          <input
            inputMode="numeric"
            placeholder="id"
            value={bancoId}
            onChange={(e) => filtros.filtrar(setBancoId)(e.target.value.replace(/\D/g, ""))}
            style={{ ...ESTILO_CAMPO, width: 100 }}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
          Ambiente
          <select
            value={ambiente}
            onChange={(e) => filtros.filtrar(setAmbiente)(e.target.value as "" | "production" | "sandbox")}
            style={ESTILO_CAMPO}
          >
            <option value="">Todos</option>
            <option value="production">Produção</option>
            <option value="sandbox">Homologação</option>
          </select>
        </label>
      </FilterBar>

      {totais ? (
        <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 14 }}>
          {totais.total.toLocaleString("pt-BR")} chamadas no recorte
          {" · "}
          {totais.http2xx.toLocaleString("pt-BR")} 2xx
          {" · "}
          {totais.http4xx.toLocaleString("pt-BR")} 4xx
          {" · "}
          {totais.http5xx.toLocaleString("pt-BR")} 5xx
          {data.data?.truncado ? " · lista truncada em 10.000 linhas — estreite o período ou a consignatária." : ""}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(r) => `${r.dia}|${r.bancoId}|${r.tokenId}|${r.metodo}|${r.rota}|${r.ambiente}`}
        loading={data.isLoading}
        emptyState={
          data.isError
            ? "Não foi possível carregar o uso da API."
            : <EmptyState
                motivo={filtros.temFiltro ? motivoVazio(1, linhas.length) : motivoVazio(linhas.length, linhas.length)}
                filtroVazio="Nenhuma chamada nesse recorte."
                semDados="Nenhuma chamada autenticada no período."
                onLimpar={filtros.limparFiltros}
              />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="linhas"
      />
    </div>
  );
}
